import React from 'react';

/**
 * Shared utilities for data table columns.
 * Extracted from columns-correspondence.tsx for reuse across column definitions.
 */

import { isSameDay, isWithinInterval, parseISO } from "date-fns";
import { Row } from "@tanstack/react-table";
import { isImageFileName, isPowerPointFileName, isSpreadsheetFileName } from "@/utils/file-utils";

/**
 * Custom filter function for date columns that handles timezone correctly.
 * Supports single date or date range filtering.
 */
export function customDateFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: { startDate?: Date; endDate?: Date } | null
): boolean {
  // No filter applied - show all rows
  if (!filterValue) return true;

  // Get the date value from the row
  const dateValue = row.getValue(columnId);

  // Handle empty or invalid dates
  if (!dateValue || (typeof dateValue === "number" && isNaN(dateValue))) {
    return false;
  }

  // Convert to Date object, handling ISO strings with timezone
  let rowDate: Date;
  try {
    if (typeof dateValue === "string") {
      // Try to parse as ISO string
      try {
        const parsedDate = parseISO(dateValue);
        if (!isNaN(parsedDate.getTime())) {
          rowDate = parsedDate;
        } else {
          rowDate = new Date(dateValue);
        }
      } catch {
        rowDate = new Date(dateValue);
      }
    } else {
      rowDate = new Date(dateValue as string | number | Date);
    }
  } catch (e) {
    console.error("Error parsing date:", e);
    return false;
  }

  // Return false if we couldn't parse a valid date
  if (!rowDate || isNaN(rowDate.getTime())) {
    return false;
  }

  const { startDate, endDate } = filterValue;

  // Both start and end date - check if date is in range
  if (startDate && endDate) {
    // Set time components for inclusive range comparison
    const startDateMidnight = new Date(startDate);
    startDateMidnight.setHours(0, 0, 0, 0);

    const endDateEndOfDay = new Date(endDate);
    endDateEndOfDay.setHours(23, 59, 59, 999);

    return isWithinInterval(rowDate, {
      start: startDateMidnight,
      end: endDateEndOfDay,
    });
  }

  // Just start date - check if date is on the same day
  if (startDate) {
    return isSameDay(rowDate, startDate);
  }

  return true;
}

/** True when a table date cell has no sortable date. */
export function isMissingDate(value: unknown): boolean {
  if (value == null || value === "") return true;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  if (typeof value === "string") return Number.isNaN(new Date(value).getTime());
  return false;
}

/** Column value for date sorting/filtering: missing or invalid dates become `undefined`. */
export function dateColumnValue(row: { date?: unknown }): Date | string | undefined {
  return isMissingDate(row.date) ? undefined : (row.date as Date | string);
}

function toTimestamp(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") return new Date(value).getTime();
  return Number.NaN;
}

/**
 * Custom sorting functions for table columns.
 */
export const sortingFunctions = {
  /**
   * Date sorting function that handles various date formats.
   * Missing/invalid dates compare as last in ascending order. Date columns should
   * also set `sortUndefined: 'last'` so they stay last when the table inverts
   * for the default date-desc sort.
   */
  dateSorting: <TData,>(rowA: Row<TData>, rowB: Row<TData>, columnId: string): number => {
    const valueA = rowA.getValue(columnId);
    const valueB = rowB.getValue(columnId);
    const missingA = isMissingDate(valueA);
    const missingB = isMissingDate(valueB);

    if (missingA && missingB) return 0;
    if (missingA) return 1;
    if (missingB) return -1;

    return toTimestamp(valueA) - toTimestamp(valueB);
  },
};

/**
 * Extract individual terms from a full-text search query for passage highlighting.
 * Search submission still uses the original query unchanged.
 */
export function getSearchHighlightTerms(
  query: string | null | undefined
): string[] | undefined {
  if (!query) return undefined;

  const terms = query.match(/[\p{L}\p{N}]+/gu) ?? [];
  const seen = new Set<string>();
  const uniqueTerms = terms.filter((term) => {
    const normalizedTerm = term.toLocaleLowerCase();
    if (seen.has(normalizedTerm)) return false;
    seen.add(normalizedTerm);
    return true;
  });

  return uniqueTerms.length > 0 ? uniqueTerms : undefined;
}

/**
 * Highlights occurrences of search queries within text.
 * Returns JSX with highlighted spans for matching text.
 *
 * @param text - The text to search within
 * @param queries - Array of search terms to highlight
 * @returns JSX element with highlighted matches
 */
export function highlightText(
  text: string,
  queries: string[] | undefined,
  options: { matchMode?: "substring" | "word-prefix" } = {},
): React.JSX.Element {
  if (!queries || queries.length === 0) return <>{text}</>;

  // Filter out empty queries
  const filteredQueries = queries.filter((query) => query.trim() !== "");
  if (filteredQueries.length === 0) return <>{text}</>;

  const orderedQueries = [...filteredQueries].sort(
    (left, right) => Array.from(right).length - Array.from(left).length,
  );

  const escapeRegex = (query: string) =>
    query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Search passages use token-aware matching. Short terms and identifiers must
  // be complete tokens; longer alphabetic terms may match inflected suffixes.
  const patterns = orderedQueries.map((query) => {
    const escapedQuery = escapeRegex(query);
    if (options.matchMode !== "word-prefix") return escapedQuery;

    const startsAtTokenBoundary = "(?<![\\p{L}\\p{N}])";
    const isLongAlphabeticTerm =
      Array.from(query).length >= 4 && /^\p{L}+$/u.test(query);
    const endsAtTokenBoundary = isLongAlphabeticTerm
      ? ""
      : "(?![\\p{L}\\p{N}])";

    return `${startsAtTokenBoundary}${escapedQuery}${endsAtTokenBoundary}`;
  });

  // Combine all queries into a single regular expression.
  const regex = new RegExp(
    patterns.join("|"),
    options.matchMode === "word-prefix" ? "giu" : "gi",
  );

  // Match all occurrences
  const matches = Array.from(text.matchAll(regex));
  const highlightedElements: React.JSX.Element[] = [];

  let lastIndex = 0;
  matches.forEach((match, index) => {
    const matchStartIndex = match.index || 0;

    // Add non-highlighted text before the match
    if (lastIndex < matchStartIndex) {
      highlightedElements.push(
        <span key={`text-${index}`}>{text.slice(lastIndex, matchStartIndex)}</span>
      );
    }

    // Add the highlighted match
    highlightedElements.push(
      <span
        key={`highlight-${index}`}
        style={{ backgroundColor: "#FFEA00", color: "black" }}
      >
        {match[0]}
      </span>
    );

    lastIndex = matchStartIndex + match[0].length;
  });

  // Add any remaining non-highlighted text
  if (lastIndex < text.length) {
    highlightedElements.push(
      <span key="remaining-text">{text.slice(lastIndex)}</span>
    );
  }

  return <>{highlightedElements}</>;
}

// Default color palette for tags
const DEFAULT_TAG_PALETTE = [
  "#EF4444", // red
  "#F97316", // orange
  "#EAB308", // yellow
  "#22C55E", // green
  "#06B6D4", // cyan
  "#3B82F6", // blue
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#6366F1", // indigo
  "#14B8A6", // teal
];

/**
 * Generates a color map for tags from document data.
 * Tags with stored colors use those; others get assigned from the palette.
 *
 * @param data - Array of documents with tag_colors property
 * @param palette - Optional custom color palette
 * @returns Record mapping tag names to colors
 */
export function generateTagColorMap<T extends { tags?: string[]; tag_colors?: Record<string, string> }>(
  data: T[],
  palette: string[] = DEFAULT_TAG_PALETTE
): Record<string, string> {
  const colorMap: Record<string, string> = {};
  const usedColors = new Set<string>();
  let paletteIndex = 0;

  // First pass: collect all stored colors
  data.forEach((item) => {
    if (item.tag_colors) {
      Object.entries(item.tag_colors).forEach(([tag, color]) => {
        if (color && !colorMap[tag]) {
          colorMap[tag] = color;
          usedColors.add(color);
        }
      });
    }
  });

  // Second pass: assign palette colors to uncolored tags
  data.forEach((item) => {
    if (item.tags) {
      item.tags.forEach((tag) => {
        if (!colorMap[tag]) {
          // Find next available palette color
          while (usedColors.has(palette[paletteIndex % palette.length]) && paletteIndex < palette.length * 2) {
            paletteIndex++;
          }
          const color = palette[paletteIndex % palette.length];
          colorMap[tag] = color;
          usedColors.add(color);
          paletteIndex++;
        }
      });
    }
  });

  return colorMap;
}

/**
 * Computes the file type category for filtering based on filename and original file type.
 * This is a pure function that derives the file type without mutating any state.
 *
 * Returns an array to match the expected format for the faceted filter component,
 * which iterates over array values for Tags, Attachments, To, From, and File type columns.
 *
 * @param fileName - The document's file name
 * @param originalFileType - The original file type from the document (e.g., "Email with attachments")
 * @returns Array with single file type string for filtering (e.g., ["PDF"], ["Word"], ["Image"])
 */
export function computeFileType(fileName: string, originalFileType: string): string[] {
  const lowerFileName = fileName.toLowerCase();

  // Check for Word documents
  if (lowerFileName.endsWith('.docx') || lowerFileName.endsWith('.doc')) {
    return ['Word'];
  }

  if (isPowerPointFileName(lowerFileName)) {
    return ['PowerPoint'];
  }

  // Check for spreadsheets
  if (isSpreadsheetFileName(lowerFileName)) {
    return ['Excel'];
  }

  if (isImageFileName(fileName)) {
    return ['Image'];
  }

  // Check for PDF
  if (lowerFileName.endsWith('.pdf')) {
    return ['PDF'];
  }

  // Check for email types
  if (lowerFileName.endsWith('.msg') || lowerFileName.endsWith('.eml')) {
    return originalFileType === 'Email with attachments' ? ['Email with attachments'] : ['Email'];
  }

  // Fallback based on original file type
  if (originalFileType === 'Email with attachments') {
    return ['Email with attachments'];
  }
  if (originalFileType === 'Email') {
    return ['Email'];
  }
  if (originalFileType === 'PDF') {
    return ['PDF'];
  }
  if (originalFileType === 'Word') {
    return ['Word'];
  }
  if (originalFileType === 'PowerPoint') {
    return ['PowerPoint'];
  }
  if (originalFileType === 'Excel') {
    return ['Excel'];
  }
  if (originalFileType === 'Image') {
    return ['Image'];
  }

  return ['Other'];
}

const parseHexColor = (hex: string): { r: number; g: number; b: number } | null => {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!match) return null;
  return {
    r: parseInt(match[1], 16),
    g: parseInt(match[2], 16),
    b: parseInt(match[3], 16),
  };
};

/**
 * Builds a subtle tag style — a pale tint of the tag color as the background
 * with a darkened version of that color as the text. This echoes the muted
 * `bg-*-50` / `text-*-800` treatment used for deadline status badges, rather
 * than filling the whole pill with the vivid stored color.
 */
export const getSubtleTagStyle = (color: string): { backgroundColor: string; color: string } => {
  const rgb = parseHexColor(color);
  if (!rgb) {
    return { backgroundColor: "#F3F4F6", color: "#374151" };
  }
  const { r, g, b } = rgb;
  const darken = 0.45;
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.22)`,
    color: `rgb(${Math.round(r * (1 - darken))}, ${Math.round(g * (1 - darken))}, ${Math.round(b * (1 - darken))})`,
  };
};

// Module-level tag color map state
let tagColorMap: Record<string, string> = {};

/**
 * Gets the current tag color map.
 */
export const getTagColorMap = (): Record<string, string> => tagColorMap;

/**
 * Updates the tag color map with new document data.
 *
 * @param data - Array of documents with tag_colors property
 * @param palette - Optional custom color palette
 */
export function updateTagColorMap<T extends { tags?: string[]; tag_colors?: Record<string, string> }>(
  data: T[],
  palette?: string[]
): void {
  tagColorMap = generateTagColorMap(data, palette);
}
